import React, { createContext, useContext, type ReactNode, useMemo } from "react";

import { usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";

type Organization = {
  id: number;
  name: string;
  description?: string;
  website?: string;
  logo?: string;
  [key: string]: any;
};

type OrganizationsContextValue = {
  organizations: Organization[];
  isLoading: boolean;
  status: number | string;
  reloadOrganizations: () => Promise<void>;
};

const OrganizationsContext = createContext<OrganizationsContextValue | undefined>(
  undefined
);

export function OrganizationsProvider({ children }: { children: ReactNode }) {
  const { route, method } = Endpoints.GetAllOrganizations();
  const { data, isLoading, status, reloadData } = usePerformRequest<any>({
    route,
    method: method as any,
  });

  const organizations = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.results && Array.isArray(data.results)) return data.results;
    return [];
  }, [data]);

  const value: OrganizationsContextValue = {
    organizations,
    isLoading,
    status,
    reloadOrganizations: reloadData,
  };

  return (
    <OrganizationsContext.Provider value={value}>
      {children}
    </OrganizationsContext.Provider>
  );
}

export function useOrganizationsContext() {
  const context = useContext(OrganizationsContext);
  if (!context) {
    throw new Error(
      "useOrganizationsContext must be used within an OrganizationsProvider"
    );
  }

  return context;
}

